/** @jsx jsx */
import { jsx } from '@emotion/react';

import { type ReactNode } from 'react';

import { PageConfig_PageName as PageName } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import DegreeCollectionProvider from 'bundles/browse-common/providers/DegreeCollectionProvider';
import DomainEntityProvider from 'bundles/browse-common/providers/DomainEntityProvider';
import DomainListProvider from 'bundles/browse-common/providers/DomainListProvider';
import DomainSearchProvider from 'bundles/browse-common/providers/DomainSearchProvider';
import DomainValuePropositionProvider from 'bundles/browse-common/providers/DomainValuePropositionProvider';
import RelatedSkillsProviderV2 from 'bundles/browse-common/providers/RelatedSkillsProviderV2';
import type { KnownDomainSlugs } from 'bundles/browse-common/types/domain';
import { isKnownDomainSlug } from 'bundles/browse-common/utils/domain-utils';
import { toSearchTopic } from 'bundles/browse-common/utils/toSearchTopic';
import { NotFound } from 'bundles/common/components/withNotFound';
import ComponentsConfigProvider from 'bundles/page-config-common/providers/ComponentsConfigProvider';
import LearnerProfileProvider from 'bundles/page-config-common/providers/consumer/LearnerProfileProvider';

function toAdditionalInputs(domainSlug: KnownDomainSlugs) {
  const searchTopic = domainSlug ? toSearchTopic(domainSlug) : undefined;

  return searchTopic
    ? // eslint-disable-next-line camelcase
      { domain_name: searchTopic }
    : undefined;
}

type Props = { children: ReactNode };

const DomainV2Provider = ({ children }: Props) => {
  const { domainSlug } = useParams();

  // we short-circuit based on "known providers" to get NotFound without a data load
  if (!isKnownDomainSlug(domainSlug)) {
    return <NotFound />;
  }

  return (
    <DomainListProvider>
      <ComponentsConfigProvider pageName={PageName.BrowseDomain} additionalInput={toAdditionalInputs(domainSlug)}>
        <DomainEntityProvider domainSlug={domainSlug}>
          <DomainValuePropositionProvider domainSlug={domainSlug}>
            <RelatedSkillsProviderV2 domainSlug={domainSlug}>
              <DomainSearchProvider domainSlug={domainSlug}>
                <LearnerProfileProvider ignorePathwaysRequirement={true}>
                  <DegreeCollectionProvider domainSlug={domainSlug}>{children}</DegreeCollectionProvider>
                </LearnerProfileProvider>
              </DomainSearchProvider>
            </RelatedSkillsProviderV2>
          </DomainValuePropositionProvider>
        </DomainEntityProvider>
      </ComponentsConfigProvider>
    </DomainListProvider>
  );
};

export default DomainV2Provider;
